import React, { useMemo, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Paper,
  Typography,
  Divider,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { SaveButton } from "@/components/ui/buttons/save-button";
import { ProductBasicInformationSection } from "@/components/products/form/product-basic-info";
import { ProductMediaFormSection } from "@/components/products/form/product-media-form-section";
import { ProductSettingsFormSection } from "@/components/products/form/product-settings-form-section";
import { ProductVariantFormSection } from "@/components/products/form/product-variant-form-section";
import {
  useGetProduct,
  useUpdateProduct,
  useProductWithRelation,
} from "@/services/product-service";
import type { Product, ProductFormValues } from "@/types/product-type";
import { productSchema } from "@/types/product-type";

export default function EditProductPage() {
  const params = useParams();
  const [, setLocation] = useLocation();
  const productId = params.id as string | undefined;

  const {
    data: productData,
    isLoading,
    error,
  } = useGetProduct(productId ?? null);

  const product = productData as Product | undefined;

  // Get product with relation for variant stats
  const { data: productWithRelation } = useProductWithRelation(productId ?? null);
  const productVariants = productWithRelation?.productVariants ?? [];

  const totalStock = productVariants.reduce((sum, v) => sum + (v.stock?.stockInHand ?? 0), 0);
  const avgPrice =
    productVariants.length > 0
      ? productVariants.reduce((sum, v) => sum + (v.price ?? product?.price ?? 0), 0) /
      productVariants.length
      : product?.price ?? 0;

  const updateProductMutation = useUpdateProduct();

  const defaultValues = useMemo<ProductFormValues>(
    () => ({
      code: product?.code ?? "",
      name: product?.name ?? "",
      description: product?.description ?? "",
      unit: product?.unit ?? "",
      price: product?.price ?? 0,
      cost: product?.cost ?? 0,
      imageUrl: product?.imageUrl ?? "",
      isActive: product?.isActive ?? false,
      onStoreFront: product?.onStoreFront ?? false,
      categoryId: product?.categoryId ? String(product.categoryId) : null,
      initStock: 0,
    }),
    [product]
  );

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    mode: "onChange",
    defaultValues,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = form;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleFormSubmit = async (values: ProductFormValues) => {
    if (!productId) throw new Error("Product id is required");
    await updateProductMutation.execute({ id: productId, data: values });
  };

  const handleSaveAndAddNew = async (values: ProductFormValues) => {
    if (!productId) throw new Error("Product id is required");
    const result = await updateProductMutation.execute({ id: productId, data: values });
    if (result.success) {
      setLocation("/products/new");
    }
  };

  const handleSave = () => {
    handleSubmit(handleFormSubmit)();
  };

  if (isLoading) {
    return (
      <Box sx={{ height: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !product || !productId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <AlertTitle>Unable to load product</AlertTitle>
          {error?.message || "Please try again later."}
        </Alert>
        <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setLocation("/products")}>
          Back to list
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Page header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#0f172a">
            Edit {product.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Update product information and details
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleSubmit(handleSaveAndAddNew)}
            disabled={isSubmitting}
            color="primary"
          >
            {isSubmitting ? "Saving..." : "Save & Add New"}
          </Button>
          <SaveButton 
            handleSave={handleSave} 
            disabled={isSubmitting} 
            label={isSubmitting ? "Saving..." : "Save"} 
          />
        </Box>
      </Box>

      {/* Two-column layout */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 300px" }, gap: 3, alignItems: "start" }}>
        {/* LEFT: form sections */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Basic Information */}
          <Paper elevation={0} sx={{ p: 3, border: "1px solid #e2e8f0", borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={0.5}>
              Basic Information
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2.5}>
              Provide essential product details.
            </Typography>
            <ProductBasicInformationSection
              control={control as any}
              register={register as any}
              errors={errors}
              inline
            />
          </Paper>

          {/* Media Management */}
          <Paper elevation={0} sx={{ p: 3, border: "1px solid #e2e8f0", borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={0.5}>
              Media Management
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2.5}>
              Add product images to showcase your product.
            </Typography>
            <ProductMediaFormSection
              control={control as any}
              register={register as any}
              errors={errors}
              setValue={setValue as any}
              watch={watch as any}
              inline
            />
          </Paper>

          {/* Variants */}
          <Paper elevation={0} sx={{ p: 3, border: "1px solid #e2e8f0", borderRadius: 3 }}>
            <ProductVariantFormSection
              watch={watch as any}
              mode="update"
              control={control as any}
            />
          </Paper>
        </Box>

        {/* RIGHT: sidebar */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Settings */}
          <Paper elevation={0} sx={{ p: 3, border: "1px solid #e2e8f0", borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Settings
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Product is active
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Make product visible in your store
                  </Typography>
                </Box>
                <ProductSettingsFormSection control={control as any} inline fieldName="isActive" />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Telegram Mini App
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Display on Telegram Mini App
                  </Typography>
                </Box>
                <ProductSettingsFormSection control={control as any} inline fieldName="onStoreFront" />
              </Box>
            </Box>
          </Paper>

          {/* Product Summary */}
          <Paper elevation={0} sx={{ p: 3, border: "1px solid #e2e8f0", borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Product Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {[
              {
                label: "Total Variants",
                value: String(productVariants.length),
                bold: true,
              },
              {
                label: "Total Stock",
                value: `${totalStock} units`,
              },
              {
                label: "Avg. Price",
                value: `$${avgPrice.toFixed(2)}`,
              },
            ].map((row) => (
              <Box
                key={row.label}
                sx={{ display: "flex", justifyContent: "space-between", py: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {row.label}
                </Typography>
                <Typography variant="body2" fontWeight={row.bold ? 700 : 500} color="#0f172a">
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
